import { useEffect, useState } from 'react';

import {
  backToPlanning,
  type ContentStatus,
  contentPath,
  fetchPlanning,
  fetchProduction,
  fetchRetryPlan,
  failureStageLabel,
  leaveProductionQueue,
  type Planning,
  type Production,
  type RetryPlan,
  stopProduction,
} from '../api.ts';
import { DASHBOARD_POLL_MS, describeError, useNow } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { Link } from '../router.tsx';
import { countdown, localTime } from '../schedule.ts';
import { useToast } from '../toast.tsx';
import { Facts, Loader } from '../ui.tsx';
import * as wording from '../wording.ts';
import { cardLine } from './lines.ts';
import type { ContentCard } from './model.ts';

/**
 * The statuses this section speaks for: Planning, and everything in
 * Production. Not `review`, `delivered` or `rejected`: those are past
 * production.
 */
const STAGE_STATUSES: ReadonlySet<ContentStatus> = new Set([
  'pending',
  'ready',
  'producing',
  'waiting',
  'approval',
  'failed',
]);

export function hasStageSection(card: ContentCard): boolean {
  return STAGE_STATUSES.has(card.content.status);
}

/**
 * Where a content stands in planning and production (US-021), and the
 * actions beside its primary one. The primary action (Fetch again, Mark
 * ready, Start production, Resume now, Open to approve, Retry or Raise the
 * cap) is the drawer's button under the header and is not repeated here.
 * Dispatches on the status, not the column: a content failed at setup stands
 * in Planning but is a failure like any other.
 */
export function StageSection({
  card,
  onChanged,
}: {
  readonly card: ContentCard;
  readonly onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const content = card.content;
  const [busy, setBusy] = useState(false);

  const secondary = async (work: () => Promise<unknown>, done: string): Promise<void> => {
    setBusy(true);
    try {
      await work();
      toast.ok(done);
      await onChanged();
    } catch (cause: unknown) {
      toast.error(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  let body;
  switch (content.status) {
    case 'pending':
      body = <PlanningState card={card} />;
      break;
    case 'ready':
      body = (
        <>
          <StateLine card={card} />
          {content.steps.total > 0 && <p className="muted">{wording.boardStepCount(content.steps.total)}</p>}
          <div className="board-drawer__actions">
            {content.queuePosition === null ? (
              // A queued content leaves the queue first: the content page offers the same one way out.
              <button
                type="button"
                className="button"
                disabled={busy}
                onClick={() =>
                  void secondary(() => backToPlanning(content.id), wording.boardBackToPlanning(content.name))
                }
              >
                <Icon name="terminal" />
                {wording.BACK_TO_PLANNING}
              </button>
            ) : (
              <button
                type="button"
                className="button"
                disabled={busy}
                onClick={() =>
                  void secondary(() => leaveProductionQueue(content.id), wording.boardLeftQueue(content.name))
                }
              >
                <Icon name="stop" />
                {wording.BOARD_LEAVE_QUEUE}
              </button>
            )}
          </div>
        </>
      );
      break;
    case 'producing':
      body = (
        <>
          <ProducingState card={card} />
          <div className="board-drawer__actions">
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => void secondary(() => stopProduction(content.id), wording.boardStopped(content.name))}
            >
              <Icon name="stop" />
              {wording.BOARD_STOP_PRODUCTION}
            </button>
          </div>
        </>
      );
      break;
    case 'waiting':
      body = (
        <>
          <StateLine card={card} />
          <HoldLifts until={content.waitingUntil} />
        </>
      );
      break;
    case 'approval':
      body = (
        <>
          <StateLine card={card} />
          {content.gate !== null && <p className="muted">{wording.boardGate(content.gate)}</p>}
        </>
      );
      break;
    case 'failed':
      body = <FailedState card={card} />;
      break;
    default:
      return null;
  }

  return (
    <section className="board-drawer__section">
      <h3 className="board-drawer__section-title">{wording.BOARD_STAGE}</h3>
      {body}
    </section>
  );
}

/** The card's own sub-state line (§ Card lines), so drawer and card never disagree. */
function StateLine({ card }: { readonly card: ContentCard }) {
  const line = cardLine(card);
  return line.loader ? (
    <Loader size="sm" label={line.text} />
  ) : (
    <p className="board-drawer__state">{line.text}</p>
  );
}

/**
 * Planning: the sub-state line, the plan's problem count when it has errors
 * (`GET /api/contents/:id/planning`, read when the drawer opens and again
 * whenever the board sees the content change), and the way to the terminal.
 */
function PlanningState({ card }: { readonly card: ContentCard }) {
  const content = card.content;
  const [planning, setPlanning] = useState<Planning | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchPlanning(content.id, controller.signal)
      .then(setPlanning)
      .catch(() => {
        // Without it the drawer still has the line; the content page has the plan.
      });
    return () => {
      controller.abort();
    };
  }, [content.id, content.updatedAt]);

  const problems = planning !== null && planning.plan.exists ? planning.plan.errors.length : 0;

  return (
    <>
      <StateLine card={card} />
      {problems > 0 && <p className="field__hint field__hint--error">{wording.boardPlanProblems(problems)}</p>}
      <div className="board-drawer__actions">
        <Link className="button" href={contentPath(content.id)}>
          <Icon name="terminal" />
          {wording.BOARD_OPEN_PLANNING_TERMINAL}
        </Link>
      </div>
    </>
  );
}

/**
 * Producing: "Part {n} of {total}" and the current step's title, from
 * `GET /api/contents/:id/production` polled every 3 s while the drawer is
 * open (and visible). Counted as the content page counts it; until the first
 * answer, from the content's own step counts.
 */
function ProducingState({ card }: { readonly card: ContentCard }) {
  const content = card.content;
  const [production, setProduction] = useState<Production | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const load = (): void => {
      fetchProduction(content.id, controller.signal)
        .then(setProduction)
        .catch(() => {
          // The next beat tries again; the line falls back to the content's counts.
        });
    };
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [content.id]);

  // An automatic revision round says so instead (§ Card lines).
  if (content.autoReview !== null) return <StateLine card={card} />;

  const steps = production?.steps ?? [];
  // The loop names its step; between two of its answers, the step marked in progress is the one.
  const current =
    steps.find((step) => step.stepId === production?.currentStepId) ??
    steps.find((step) => step.status === 'in-progress') ??
    null;
  const total = steps.length > 0 ? steps.length : content.steps.total;
  const done = steps.length > 0 ? steps.filter((step) => step.status === 'done').length : content.steps.done;
  const at = current === null ? Math.min(done + 1, total) : steps.indexOf(current) + 1;

  if (total === 0) return <Loader size="sm" label={wording.MAKING} />;
  return (
    <Loader
      size="sm"
      label={wording.boardPartOf(at, total)}
      {...(current === null ? {} : { detail: current.title })}
    />
  );
}

/** Waiting: when Claude's usage-limit hold lifts, counted down. */
function HoldLifts({ until }: { readonly until: string | null }) {
  const now = useNow();
  if (until === null) return <p className="muted">{wording.BOARD_HOLD_LIFTS_UNKNOWN}</p>;
  return <p className="muted">{wording.boardHoldLifts(countdown(until, now), localTime(until))}</p>;
}

/**
 * Failed: the stage and `lastError`, and what **Retry** would do in the
 * server's words (`GET /api/contents/:id/retry`). A spend-cap failure is not
 * retried from here: its primary action goes to the content page to raise the
 * cap.
 */
function FailedState({ card }: { readonly card: ContentCard }) {
  const content = card.content;
  const budget = content.failureStage === 'budget';
  const [plan, setPlan] = useState<RetryPlan | null>(null);

  useEffect(() => {
    if (budget) return undefined;
    const controller = new AbortController();
    fetchRetryPlan(content.id, controller.signal)
      .then(setPlan)
      .catch(() => {
        // Retry still works without the description; the content page has it.
      });
    return () => {
      controller.abort();
    };
  }, [content.id, content.updatedAt, budget]);

  const stage =
    plan?.stageLabel ?? (content.failureStage === null ? null : failureStageLabel(content.failureStage));

  return (
    <>
      <Facts
        items={[
          { label: wording.BOARD_FAILED_STAGE, value: stage ?? <span className="muted">—</span> },
          {
            label: wording.BOARD_FAILED_ERROR,
            value: content.lastError ?? <span className="muted">—</span>,
          },
        ]}
      />
      {budget ? (
        <p className="muted">{wording.BOARD_RETRY_RAISE_CAP}</p>
      ) : (
        plan !== null && (
          <p className="muted">
            {plan.retryable ? wording.boardRetryPlan(plan.label, plan.summary) : plan.blocked}
          </p>
        )
      )}
    </>
  );
}
